import type { CommandProps, Commands, Editor, Range } from '@tiptap/core';
import { createNodeFromContent } from '@tiptap/core';
import type { Fragment, Node } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import type { Step } from '@tiptap/pm/transform';
import { ReplaceStep } from '@tiptap/pm/transform';

import type { ResultContent } from '../types';

const isFragment = (nodeOrFragment: Node | Fragment): nodeOrFragment is Fragment => {
  return nodeOrFragment.toString().startsWith('<');
};

const isReplaceStep = (step: Step): step is ReplaceStep => {
  return step instanceof ReplaceStep;
};

/**
 * Inserts AI generated content at provided position.
 * Basically is a copy of `insertContentAt` command with modifications for selection after insert is done.
 * https://github.com/ueberdosis/tiptap/blob/6b65af8fc31ffbbcf79b89bfdaceee7aadbf3f27/packages/core/src/commands/insertContentAt.ts
 */
export const insertAiGeneratedContent: Commands['ai']['insertAiGeneratedContent'] =
  (value, options) =>
  ({ tr, dispatch, editor }: CommandProps) => {
    const position: Range = { from: tr.selection.from, to: tr.selection.to };

    if (options?.position === 'before') {
      position.to = position.from;
    }

    if (options?.position === 'after') {
      position.from = position.to;
    }

    if (dispatch) {
      insertContent({
        value,
        tr,
        editor,
        position,
        options,
      });

      // Setting selection to inserted content seems to be error-prone.
      // If calculation off by one issue this will throw and error.
      // Instead of failing whole transaction which inserts content,
      // we try setting selection to inserted content. If we fail ¯\_(ツ)_/¯
      try {
        setSelectionToInsertedContent(tr);
      } catch (e) {
        console.error('Error setting selection to inserted content', e);
      }
    }

    return true;
  };

const insertContent = ({
  value,
  tr,
  editor,
  position,
  options,
}: {
  value: ResultContent;
  tr: Transaction;
  editor: Editor;
  position: Range;
  options?: { position?: 'before' | 'after' | 'replace' };
}) => {
  const contentValue =
    value.plainContent === value.htmlContent ? value.plainContent : value.content;
  const content = createNodeFromContent(contentValue, editor.schema);

  // don’t dispatch an empty fragment because this can lead to strange errors
  if (content.toString() === '<>') {
    return true;
  }

  let { from, to } = position;

  let isOnlyTextContent = true;
  let isOnlyBlockContent = true;
  const nodes = isFragment(content) ? content : [content];

  nodes.forEach(node => {
    // check if added node is valid
    node.check();

    isOnlyTextContent = isOnlyTextContent ? node.isText && node.marks.length === 0 : false;

    isOnlyBlockContent = isOnlyBlockContent ? node.isBlock : false;
  });

  // check if we can replace the wrapping node by
  // the newly inserted content
  // example:
  // replace an empty paragraph by an inserted image
  // instead of inserting the image below the paragraph
  if (from === to && isOnlyBlockContent) {
    const { parent } = tr.doc.resolve(from);
    const isEmptyTextBlock = parent.isTextblock && !parent.type.spec.code && !parent.childCount;

    if (isEmptyTextBlock) {
      from -= 1;
      to += 1;
    }
  }

  const blockRange = tr.selection.$from.blockRange(tr.selection.$to);
  // if range is collapsed and current block has only 'space' in it, replacing all block
  const shouldReplaceBlock =
    tr.selection.$from === tr.selection.$to && tr.selection.$from.parent.textContent === ' ';
  const isStartOfBlock = tr.selection.$from.parentOffset === 0;
  const isEndOfBlock = tr.selection.$to.parentOffset === tr.selection.$to.parent.content.size;

  if (options?.position === 'before') {
    // should insert to the start of block range.
    // When inside nested structure (list) need to adjust offset
    const pos = blockRange?.start ?? from;
    const depth = tr.selection.$from.depth;
    const offset = depth - 1;
    tr.insert(Math.max(pos - offset, 0), content);
    return;
  }

  if (options?.position === 'after') {
    // should insert to the end of block range.
    // When inside nested structure (list) need to adjust offset
    const pos = blockRange?.end ?? to;
    const depth = tr.selection.$to.depth;
    const offset = !isOnlyTextContent || depth === 1 ? 0 : 1;
    tr.insert(pos + offset, content);
    return;
  }

  if (isOnlyTextContent) {
    // if value is string, we can use it directly
    // otherwise if it is an array, we have to join it
    if (Array.isArray(contentValue)) {
      tr.insertText(contentValue.map(v => v.text || '').join(''), from, to);
    } else if (typeof contentValue === 'object' && !!value && !!contentValue.text) {
      tr.insertText(contentValue.text, from, to);
    }
    tr.insertText(contentValue as string, from, to);
    return;
  }

  if (options?.position === 'replace') {
    const offsetForInlineReplace = canReplaceInline(tr, content) ? 1 : 0;
    const modifiedFromPosition =
      Math.max((blockRange?.start ?? from) - 1, 0) + offsetForInlineReplace;
    const modifiedEndPosition = (blockRange?.end ?? to) - offsetForInlineReplace;

    tr.replaceWith(
      shouldReplaceBlock || isStartOfBlock ? modifiedFromPosition : from,
      shouldReplaceBlock || isEndOfBlock ? modifiedEndPosition : to,
      content
    );

    return;
  }
};

const setSelectionToInsertedContent = (tr: Transaction) => {
  const lastStep = tr.steps[tr.steps.length - 1];
  if (lastStep && isReplaceStep(lastStep)) {
    tr.setSelection(
      TextSelection.between(
        tr.doc.resolve(lastStep.from),
        tr.doc.resolve(lastStep.slice.content.size + lastStep.from)
      )
    );
  }
};

const canReplaceInline = (tr: Transaction, content: Node | Fragment): boolean => {
  try {
    const isNested = tr.selection.$from.depth > 1;
    const typeName = isFragment(content)
      ? content.child(0).type.name
      : content.content.child(0).type.name;
    return isNested && typeName === tr.selection.$from.parent.type.name;
  } catch {
    // Fragment raise an error when the index is out of range.
    return false;
  }
};
